import React, { useEffect, useState } from 'react';
import { Clock, Mail, Search, Users } from 'lucide-react';
import { balanceApi } from '../../../api/balance';
import { BalanceResponse } from '../../../types/balance.types';

const getMonthlyRenewalLabel = (daysUntilReset: number): string => {
  if (daysUntilReset <= 0) {
    return 'Renews today';
  }
  if (daysUntilReset === 1) {
    return 'Renews tomorrow';
  }
  return `Renews in ${daysUntilReset} days`;
};

export const CreditBalance: React.FC = () => {
  const [balance, setBalance] = useState<BalanceResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBalance();
    const interval = setInterval(loadBalance, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadBalance = async () => {
    try {
      const data = await balanceApi.getBalance();
      setBalance(data);
    } catch (error) {
      console.error('Failed to load balance:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatResetsAt = (value: string | null) => {
    if (!value) return null;
    const resetsAt = new Date(value);
    const diffMs = resetsAt.getTime() - Date.now();
    if (Number.isNaN(resetsAt.getTime()) || diffMs <= 0) return null;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (hours <= 0) return `${minutes}m`;
    return `${hours}h ${minutes}m`;
  };

  if (loading || !balance) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-light px-3 py-2 sm:px-4">
        <div className="h-4 w-32 animate-pulse rounded bg-gray-300" />
      </div>
    );
  }

  const resetsIn = formatResetsAt(balance.search_quota_resets_at);
  const monthlyRenewalLabel = getMonthlyRenewalLabel(balance.days_until_reset);

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3">
        <div className="flex min-h-[5.5rem] flex-col rounded-lg bg-light px-3 py-2.5 sm:min-h-[6rem] sm:px-4 sm:py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2 text-sm font-semibold text-gray-700">
              <Search className="h-4 w-4 shrink-0 text-primary" />
              <span className="truncate">Searches</span>
            </div>
          </div>
          <div className="mt-1 flex flex-wrap items-end gap-x-2 gap-y-0.5">
            <span className="text-2xl font-bold text-secondary">
              {balance.searches_remaining.toLocaleString()}
            </span>
            <span className="text-sm text-gray-600">
              / {balance.daily_search_limit.toLocaleString()}
              {!balance.is_lifetime_plan && ' today'}
            </span>
          </div>
          {resetsIn && (
            <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-600">
              <Clock className="h-3.5 w-3.5 shrink-0 text-gray-500" />
              <span>Resets in {resetsIn}</span>
            </div>
          )}
        </div>

        <div className="flex min-h-[5.5rem] flex-col rounded-lg bg-light px-3 py-2.5 sm:min-h-[6rem] sm:px-4 sm:py-3">
          <div className="flex min-w-0 items-center gap-2 text-sm font-semibold text-gray-700">
            <Users className="h-4 w-4 shrink-0 text-primary" />
            <span className="leading-tight">People enrichments</span>
          </div>
          <div className="mt-1">
            <span className="text-2xl font-bold text-secondary">
              {balance.dm_unlocks_remaining.toLocaleString()}
            </span>
          </div>
          <div className="mt-1 text-xs leading-snug text-gray-600">
            {monthlyRenewalLabel}
          </div>
        </div>

        <div className="flex min-h-[5.5rem] flex-col rounded-lg bg-light px-3 py-2.5 sm:min-h-[6rem] sm:px-4 sm:py-3">
          <div className="flex min-w-0 items-center gap-2 text-sm font-semibold text-gray-700">
            <Mail className="h-4 w-4 shrink-0 text-primary" />
            <span className="leading-tight">Email verifications</span>
          </div>
          <div className="mt-1">
            <span className="text-2xl font-bold text-secondary">
              {balance.email_verifications_remaining.toLocaleString()}
            </span>
          </div>
          <div className="mt-1 text-xs leading-snug text-gray-600">
            {monthlyRenewalLabel}
          </div>
        </div>
      </div>
    </div>
  );
};
